import React, { useEffect, useState } from "react";
import {
  Form,
  Button,
  Card,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  FaUserPlus,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaLock,
  FaKey,
  FaCheckCircle,
  FaExclamationCircle,
  FaUser,
  FaEdit,
} from "react-icons/fa";
import { MdBusiness, MdAccessTime } from "react-icons/md";
import { BsBuilding } from "react-icons/bs";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Baseurl } from "../../baseurl";

const EditClient = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get client ID from URL params
  const { loginData } = useSelector((store) => store.auth);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validated, setValidated] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    organisation: "",
    availabilityStatus: true,
    workingForAgency: false,
    agency: {
      id: "",
      name: "",
      phn: "",
      email: "",
    },
  });

  // Fetch client data on component mount
  useEffect(() => {
    fetchClientData();
  }, [id]);

  // Set agency data from loginData
  useEffect(() => {
    if (!loginData || !fetchLoading) return;

    const organisationData =
      loginData.role === "admin"
        ? {
            id: loginData._id,
            email: loginData?.email,
            name: loginData.organisation,
            phn: loginData?.phone,
          }
        : {
            id: loginData.organisation?.id,
            email: loginData.organisation?.email,
            name: loginData.organisation?.name,
            phn: loginData.organisation?.phn,
          };

    setFormData((prevData) => ({
      ...prevData,
      agency: organisationData,
    }));
  }, [loginData, fetchLoading]);

  const fetchClientData = async () => {
    setFetchLoading(true);
    try {
      const response = await axios.get(`${Baseurl}/api/client/individualclient/${id}`);
      if (response.data.success) {
        const clientData = response.data.client;
        setFormData({
          name: clientData.name || "",
          phone: clientData.phone || "",
          email: clientData.email || "",
          password: "", // Don't populate password for security
          confirmPassword: "", // Don't populate confirm password
          organisation: clientData.organisation || "",
          availabilityStatus: clientData.availabilityStatus || false,
          workingForAgency: clientData.workingForAgency || false,
          agency: clientData.agency || {
            id: "",
            name: "",
            phn: "",
            email: "",
          },
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching client data");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes("agency.")) {
      const agencyField = name.split(".")[1];
      setFormData({
        ...formData,
        agency: {
          ...formData.agency,
          [agencyField]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setError("");
    setSuccess("");

    // Validate password if it's being changed
    if (formData.password || formData.confirmPassword) {
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      if (formData.password.length < 6) {
        setError("Password must be at least 6 characters long");
        return;
      }
    }

    setLoading(true);

    try {
      // Create update data object
      const updateData = { ...formData };
      
      // Remove confirmPassword from update data
      delete updateData.confirmPassword;
      
      // If password is empty, remove it from update data (don't change password)
      if (!updateData.password) {
        delete updateData.password;
      }

      console.log(updateData, "updateData");
      
      const response = await axios.put(
        `${Baseurl}/api/client/updateclient/${id}`,
        updateData,
      );
      
      if (response.data.success) {
        setSuccess("Client updated successfully!");
        // Redirect after 2 seconds
        setTimeout(() => {
          navigate("/clients/list");
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error updating client");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading client data...</p>
      </div>
    );
  }

  return (
    <div fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="d-flex align-items-center">
                <FaEdit className="me-3" />
                Edit Client
              </h2>
              <p className="text-muted">
                Update client information and details
              </p>
            </div>
          </div>
        </Col>
      </Row>
      <Row className="">
        <Col lg={12}>
          <Card className="shadow border-0">
            <Card.Header className="bg-warning text-white py-3">
              <div className="d-flex align-items-center">
                <FaEdit size={24} className="me-2" />
                <div>
                  <h4 className="mb-0">Edit Client: {formData.name}</h4>
                  <small className="text-white-50">
                    Update client account details in the system
                  </small>
                </div>
              </div>
            </Card.Header>

            <Card.Body className="p-4">
              {/* Error Alert */}
              {error && (
                <Alert variant="danger" className="d-flex align-items-center">
                  <FaExclamationCircle className="me-2" size={20} />
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert variant="success" className="d-flex align-items-center">
                  <FaCheckCircle className="me-2" size={20} />
                  {success}
                </Alert>
              )}

              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <Card className="mb-4 border-0 bg-light">
                  <Card.Body>
                    <h5 className="mb-3 text-primary">
                      <FaUserPlus className="me-2" />
                      Personal Information
                    </h5>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            <FaUserPlus className="me-2 text-muted" />
                            Full Name
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter full name"
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter a name
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            <FaPhone className="me-2 text-muted" />
                            Phone Number
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Enter phone number"
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter a phone number
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            <FaEnvelope className="me-2 text-muted" />
                            Email Address
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter email address"
                            required
                          />
                          <Form.Control.Feedback type="invalid">
                            Please enter a valid email
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            <FaBuilding className="me-2 text-muted" />
                            Organisation
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="organisation"
                            value={formData.organisation}
                            onChange={handleChange}
                            placeholder="Enter organisation name"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Agency Details Section */}
                <Card className="mb-4 border-0 bg-light border-start border-primary border-3">
                  <Card.Body>
                    <h5 className="mb-3 text-primary">
                      <BsBuilding className="me-2" />
                      Agency Details
                    </h5>
              
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Agency ID</Form.Label>
                          <Form.Control
                            type="text"
                            name="agency.id"
                            value={formData.agency.id}
                            onChange={handleChange}
                            placeholder="Enter agency ID"
                            readOnly={!!loginData}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Agency Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="agency.name"
                            value={formData.agency.name}
                            onChange={handleChange}
                            placeholder="Enter agency name"
                            readOnly={!!loginData}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            <FaPhone className="me-2 text-muted" />
                            Agency Phone
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="agency.phn"
                            value={formData.agency.phn}
                            onChange={handleChange}
                            placeholder="Enter agency phone"
                            readOnly={!!loginData}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            <FaEnvelope className="me-2 text-muted" />
                            Agency Email
                          </Form.Label>
                          <Form.Control
                            type="email"
                            name="agency.email"
                            value={formData.agency.email}
                            onChange={handleChange}
                            placeholder="Enter agency email"
                            readOnly={!!loginData}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Form Actions */}
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/client/list")}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="warning"
                    disabled={loading}
                    className="px-4"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Updating...
                      </>
                    ) : (
                      <>
                        <FaEdit className="me-2" />
                        Update Client
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EditClient;